/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { MdDelete, MdAdd, MdSearch, MdImage } from "react-icons/md";
import PackageService from "../../../services/package.service";
import { Constants } from "../../../utils";
import ConfirmModal from "../../../components/confirm-modal";

const Packages = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loadingInitial, setLoadingInitial] = useState(true);

  // Estados para Modales de Confirmación
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);

  const timerRef = useRef(null);
  const listRef = useRef(null);

  const load = useCallback(
    async (reload = false, currentSearch = search) => {
      const currentPage = reload ? 1 : page;
      setLoading(true);
      if (reload) setPage(1);

      try {
        const res = await PackageService.admin.get({
          page: currentPage,
          withoutLoading: true,
          search: currentSearch,
        });

        setData((prev) =>
          reload
            ? res?.packages?.rows || []
            : [...prev, ...(res?.packages?.rows || [])],
        );
        setLastPage(res?.packages?.count || 1);
      } catch (error) {
        toast.error(
          typeof error === "string"
            ? error
            : error?.response?.data?.message ||
                error?.message ||
                "Error al cargar los BL",
        );
      } finally {
        setLoading(false);
        setLoadingInitial(false);
      }
    },
    [page, search],
  );

  useEffect(() => {
    load(true);
  }, []);

  useEffect(() => {
    if (page > 1) load();
  }, [page]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => load(true, val), 1000);
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.2 &&
      page < lastPage &&
      !loading
    ) {
      setPage((prev) => prev + 1);
    }
  };

  const openDeleteModal = (item, e) => {
    e.stopPropagation();
    setSelectedItem(item);
    setIsDeleteModalOpen(true);
  };

  const openStatusModal = (item, index, e) => {
    e.stopPropagation();
    setSelectedItem(item);
    setSelectedIndex(index);
    setIsStatusModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedItem) return;
    setLoadingDelete(true);
    try {
      await PackageService.admin.delete({ package_id: selectedItem.id });
      toast.success("Se ha eliminado el BL");
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
      load(true);
    } catch (error) {
      const errorMsg =
        typeof error === "string"
          ? error
          : error?.response?.data?.message ||
            error?.message ||
            "Error al eliminar";
      toast.error(errorMsg);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleConfirmStatus = async () => {
    if (!selectedItem) return;
    setLoadingStatus(true);
    try {
      const res = await PackageService.admin.changeStatus({
        package_id: selectedItem.id,
      });
      toast.success("Se ha cambiado el estatus del BL");
      setData((prevData) => {
        const newData = [...prevData];
        if (selectedIndex !== null) {
          newData[selectedIndex] = res.package;
        }
        return newData;
      });
      setIsStatusModalOpen(false);
      setSelectedItem(null);
      setSelectedIndex(null);
    } catch (error) {
      const errorMsg =
        typeof error === "string"
          ? error
          : error?.response?.data?.message ||
            error?.message ||
            "Error al cambiar el estatus";
      toast.error(errorMsg);
    } finally {
      setLoadingStatus(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    return status === Constants.PACKAGE_STATUS.PENDING
      ? "text-red-500"
      : "text-green-500";
  };

  return (
    <div
      className="flex flex-col h-[calc(100vh-5rem)] overflow-y-auto px-4 py-6 md:px-8"
      onScroll={handleScroll}
    >
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-1/2 lg:w-1/3">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MdSearch size={20} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Buscar BL..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-[#1D3B8B] focus:border-transparent outline-none transition-all"
            value={search}
            onChange={handleSearch}
          />
        </div>

        <button
          onClick={() => navigate("/admin/packages/create")}
          className="w-full md:w-auto flex items-center justify-center gap-2 px-5 py-2.5 bg-[#1D3B8B] text-white rounded-xl font-semibold shadow-md hover:bg-orange-600 transition-colors"
        >
          <MdAdd size={20} />
          <span>Registrar nuevo BL</span>
        </button>
      </div>

      <div
        className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
        ref={listRef}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-gray-600">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 font-semibold">Fecha</th>
                <th className="px-6 py-4 font-semibold">BL</th>
                <th className="px-6 py-4 font-semibold">Estatus</th>
                <th className="px-6 py-4 font-semibold text-center">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loadingInitial && data.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-[#1D3B8B] font-medium"
                  >
                    Cargando...
                  </td>
                </tr>
              )}

              {!loadingInitial && data.length === 0 && (
                <tr>
                  <td
                    colSpan="4"
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    No se han encontrado registros
                  </td>
                </tr>
              )}

              {data.map((item, index) => {
                const isFinished =
                  item.status === Constants.PACKAGE_STATUS.FINISH;

                return (
                  <tr
                    key={item.id || index}
                    className={`transition-colors ${
                      isFinished
                        ? "bg-gray-50/30"
                        : "hover:bg-gray-50/50 cursor-pointer"
                    }`}
                    onClick={() => {
                      if (!isFinished) {
                        navigate("/admin/packages/create", { state: { item } });
                      }
                    }}
                  >
                    <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-6 py-4 font-bold text-[#1D3B8B]">
                      {item.number}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        type="button"
                        disabled={isFinished}
                        onClick={(e) => openStatusModal(item, index, e)}
                        className={`font-bold uppercase underline hover:opacity-80 transition-opacity ${getStatusColor(
                          item.status,
                        )} ${isFinished ? "cursor-default" : "cursor-pointer"}`}
                      >
                        {item.status_text}
                      </button>
                    </td>
                    <td className="px-6 py-4 flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate("/admin/packages/photos", {
                            state: { item },
                          });
                        }}
                        className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ver fotos"
                      >
                        <MdImage size={22} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => openDeleteModal(item, e)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Eliminar BL"
                      >
                        <MdDelete size={22} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {loading && lastPage > 1 && (
          <div className="px-6 py-4 text-center text-[#1D3B8B] font-medium border-t border-gray-50 bg-gray-50/50">
            Cargando más registros...
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="¿Desea eliminar el BL?"
        message="Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
        loading={loadingDelete}
        onConfirm={handleConfirmDelete}
        onClose={() => setIsDeleteModalOpen(false)}
      />

      <ConfirmModal
        isOpen={isStatusModalOpen}
        title="¿Desea cambiar el estatus?"
        message="El estatus del BL cambiará a Finalizado."
        confirmText="Aceptar"
        variant="primary"
        loading={loadingStatus}
        onConfirm={handleConfirmStatus}
        onClose={() => setIsStatusModalOpen(false)}
      />
    </div>
  );
};

export default Packages;
